import React, { useContext } from 'react';
import { Card, CardBody, Typography } from '@one-thd/sui-atomic-components';
import { ExperienceContext, useConfigService, } from '@thd-nucleus/experience-context';
import { pushErrorEvent } from '../../../utils/analytics';
import { getLastModifiedDate } from '../../../utils/date';
import { MeatballsMenu, } from '../../shared/meatballs-menu/meatballs-menu';
import { getPaymentsErrorCode } from '../../../utils/payments';
import { useModal } from '../../../hooks/use-modal';
import { usePayments } from '../../../hooks/use-payments';
import { CardType } from '../../../constants/payments';
import { ACTIVE_GIFT_CARD_SAVES_MSG, CHANGES_SAVED_MSG, DELETE_GIFT_CARD_MSG, INACTIVE_GIFT_CARD_SAVES_MSG, } from '../../../constants/response-success-messages';
import { ERR_UNABLE_TO_SAVE } from '../../../constants/response-error-messages';
import { GiftCardLogoAndNumber } from './logo-number';
import { DisplayGiftCardBalance, } from '../display-gift-card-balance-modal/display-gift-card-balance-modal';
import { useMeatballMenuActions } from '../../../hooks/use-meatball-payment-actions';
export const GiftCard = ({ card, onActionSuccess, onActionFailure, onCheckBalaceAndReloadDrawerOpen, }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const { openModal, hideModal } = useModal();
    const { giftCards } = usePayments();
    const { channel } = useContext(ExperienceContext);
    const { onSetActive, onSetInactive, onEditNickName, onViewPurchases, onReloadGiftCard, onDeleteGiftCard, onCheckGiftCardBalance, onCheckGiftCardBalanceAndReload, } = useMeatballMenuActions();
    const { paymentId } = card;
    const isCardInactive = ((_a = card.cardStatus) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'inactive';
    const isMobile = channel === 'mobile';
    const { isB2B } = usePayments();
    const shouldUseCheckAndReloadGiftCardBalanceModal = useConfigService('ENABLE_NEW_CHECK_AND_RELOAD_GIFT_CARD_BALANCE_MODAL') &&
        isB2B;
    const handleCheckGiftCardBalance = () => {
        const onSuccess = (data) => {
            const props = {
                card,
                balance: data.availableAmount,
                hideModal,
            };
            hideModal();
            openModal(DisplayGiftCardBalance, props, {
                size: isMobile ? 'sm' : 'md',
            });
        };
        const onFailure = (errorMessage) => {
            const props = {
                card,
                balance: null,
                errorMessage,
                hideModal,
            };
            hideModal();
            openModal(DisplayGiftCardBalance, props, {
                size: isMobile ? 'sm' : 'md',
            });
        };
        onCheckGiftCardBalance(card, false, onSuccess, onFailure);
    };
    const handleEditNickName = () => {
        const currentCard = (giftCards === null || giftCards === void 0 ? void 0 : giftCards.response).find((payment) => payment.paymentId === card.paymentId);
        const onSuccess = () => {
            onActionSuccess(card.paymentId, CHANGES_SAVED_MSG.message);
        };
        const onFailure = (error) => {
            const errorCode = getPaymentsErrorCode(error);
            onActionFailure(card.paymentId, ERR_UNABLE_TO_SAVE.message);
            pushErrorEvent('edit gift card', 'inline', errorCode || 'n/a', ERR_UNABLE_TO_SAVE.message);
        };
        onEditNickName(card, currentCard, CardType.GIFT_CARD, onSuccess, onFailure);
    };
    const handleSetActive = () => {
        const onSuccess = () => {
            onActionSuccess(paymentId, ACTIVE_GIFT_CARD_SAVES_MSG.message);
        };
        const onFailure = () => {
            onActionFailure(paymentId, ERR_UNABLE_TO_SAVE.message);
        };
        onSetActive(card, CardType.GIFT_CARD, onSuccess, onFailure);
    };
    const handleSetInactive = () => {
        const onSuccess = () => {
            onActionSuccess(paymentId, INACTIVE_GIFT_CARD_SAVES_MSG.message);
        };
        const onFailure = () => {
            onActionFailure(paymentId, ERR_UNABLE_TO_SAVE.message);
        };
        onSetInactive(card, CardType.GIFT_CARD, onSuccess, onFailure);
    };
    const handleDeleteCard = () => {
        const onSuccess = () => {
            onActionSuccess(paymentId, DELETE_GIFT_CARD_MSG.message);
        };
        const onFailure = (error) => {
            const errorCode = getPaymentsErrorCode(error);
            onActionFailure(card.paymentId, ERR_UNABLE_TO_SAVE.message);
            pushErrorEvent('gift cards: delete', 'inline', errorCode || 'n/a', ERR_UNABLE_TO_SAVE.message);
        };
        onDeleteGiftCard(card, onSuccess, onFailure);
    };
    const menuItems = [
        {
            value: 'Add/Edit Nickname',
            action: handleEditNickName,
            isHidden: !((_b = card.actions) === null || _b === void 0 ? void 0 : _b.edit),
        },
        {
            value: 'View Purchases',
            action: () => {
                onViewPurchases(card, CardType.GIFT_CARD);
            },
            isHidden: !((_c = card.actions) === null || _c === void 0 ? void 0 : _c.viewPurchases),
        },
        {
            value: 'Set Active',
            action: handleSetActive,
            isHidden: !((_d = card.actions) === null || _d === void 0 ? void 0 : _d.setActive),
        },
        {
            value: 'Set Inactive',
            action: handleSetInactive,
            isHidden: !((_e = card.actions) === null || _e === void 0 ? void 0 : _e.setInactive),
        },
        {
            value: 'Check Gift Card Balance',
            action: () => {
                handleCheckGiftCardBalance();
            },
            isHidden: !!shouldUseCheckAndReloadGiftCardBalanceModal,
        },
        {
            value: 'Reload Gift Card',
            action: onReloadGiftCard,
            isHidden: !((_f = card.actions) === null || _f === void 0 ? void 0 : _f.setInactive) ||
                !!shouldUseCheckAndReloadGiftCardBalanceModal,
        },
        {
            value: 'Check Balance & Reload',
            action: () => {
                onCheckGiftCardBalanceAndReload(onCheckBalaceAndReloadDrawerOpen);
            },
            isHidden: !shouldUseCheckAndReloadGiftCardBalanceModal,
        },
        {
            value: 'Delete',
            action: handleDeleteCard,
            isHidden: !((_g = card.actions) === null || _g === void 0 ? void 0 : _g.delete),
        },
    ];
    return (React.createElement(Card, { grow: true },
        React.createElement(CardBody, { grow: true },
            React.createElement("div", { "data-testid": "gift-card", className: "sui-flex sui-flex-col sui-gap-3" },
                React.createElement("div", { className: "sui-flex sui-flex-col sui-gap-1" },
                    React.createElement("div", { className: `sui-flex sui-justify-between ${card.cardNickName ? 'sui-mb-2' : 'sui-mb-3'}` },
                        React.createElement("h3", { className: `sui-font-display sui-text-3xl sui-mr-4 sui-truncate ${isCardInactive ? 'sui-text-inactive' : 'sui-text-primary'}` }, card.cardNickName),
                        React.createElement("div", null,
                            React.createElement(MeatballsMenu, { label: "Gift Card", items: menuItems }))),
                    React.createElement("div", { className: "sui-flex sui-flex-wrap" },
                        React.createElement(GiftCardLogoAndNumber, { card: card, shouldShowInactive: true }))),
                isCardInactive && (React.createElement("div", { className: "sui-mb-6" },
                    React.createElement(Typography, { weight: "bold" }, "Inactive"))),
                React.createElement("div", null,
                    card.addedBy && (React.createElement("div", { className: "sui-overflow-hidden" },
                        React.createElement(Typography, { truncate: true, color: isCardInactive ? 'inactive' : 'subtle' },
                            "Added by ",
                            React.createElement("span", { "data-qm-mask": "" }, card.addedBy)))),
                    card.lastModifiedDate && (React.createElement(Typography, { color: isCardInactive ? 'inactive' : 'subtle' },
                        "Last edited ",
                        getLastModifiedDate(card.lastModifiedDate))))))));
};
